import { AppBar, Checkbox, createStyles, Theme, Toolbar, Typography, withStyles, WithStyles, Dialog, DialogContent, Button, DialogActions, DialogContentText } from '@material-ui/core';
import React from 'react';
import { AuthContext } from '../authentication/AuthContext';
import { SortUtils } from '../general/sorting/SortUtils';
import { DeltaXMLColors } from './styling/DeltaColors';
import { LicenseType } from './ActivateLicenseData'
import { EulaCheckBoxData } from './ActivateLicense';

const appURL = process.env.REACT_APP_SERVER_API_URL!;

const styles = (theme: Theme) =>
  createStyles({
    appBar: {
      alignItems: "left",
      backgroundColor: DeltaXMLColors.OXFORD_BLUE,
    },
    textField: {
      backgroundColor: DeltaXMLColors.CONCRETE,
      margin: theme.spacing(1),
      minwidth: 125,
      paddingStart: '5px',
      paddingEnd: '5px'
    },
    hostIdTextField: {
      margin: theme.spacing(1),
      minWidth: 260,
    },
    hostForm: {
      backgroundColor: DeltaXMLColors.CONCRETE,
      borderRadius: 5,
      margin: theme.spacing(1),
      minWidth: 125,
    },
    select: {
      marginLeft: '5px',
      marginRight: '5px'
    },
    link: {
      color: DeltaXMLColors.FOUNTAIN_BLUE,
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    contextText: {
      height: 'calc(100vh - 250px)',
      overflow: 'scroll',
      border: '1px solid #cacaca',
      padding: '0px'
    },
  });

export interface EulaCheckBoxProps extends WithStyles<typeof styles> {
  dealId: string;
  handleEulaCheckboxChange: () => void;
  handleLicenseAccepted: (licenseId: string) => void;
  handleEulaDataRequest: (data: EulaCheckBoxData) => void;
  isEulaAccepted: boolean;
  lineLicenseType: LicenseType;
  lineExpiry: string;
  eulaRequestData: EulaCheckBoxData;
}

export enum EulaAgreedPreviously {
  unknown,
  yes,
  no,
  error
}

interface LicenseAgreementRecord {
  acceptedBy: string,
  associatedDeal: string,
  customAgreement: boolean,
  dateAccepted: string,
  ipAddress: string,
  license: string,
  licenseType: LicenseAgreementType,
  latestVersion: boolean
}

interface LicenseAgreement {
  licenseId: string | null,
  content: string | null
}


enum LicenseAgreementType {
  purchased = 'Purchased',
  trial = 'Trial'
}

function EulaCheckBox(props: EulaCheckBoxProps) {
  const authContext = React.useContext(AuthContext);
  const { classes, dealId, lineLicenseType, lineExpiry, isEulaAccepted, eulaRequestData, handleEulaCheckboxChange, handleLicenseAccepted, handleEulaDataRequest } = props;
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [licenseAgreement, setLicenseAgreement] = React.useState<LicenseAgreement>({licenseId: null, content: null});
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
  }

  const fetchEulaContent = async () => {
    if (!(authContext.authVariables?.access_token && authContext.authVariables?.id_token)) {
      return;
    };
    const getRequestOptions: RequestInit = {
      method: 'GET',
      headers: {
        "Authorization": "Bearer " + authContext.authVariables?.access_token,
        "Accept": "application/json",
      }
    };
    const pathDeal = encodeURIComponent(dealId);
    const encodedURL = appURL + '/deals/' + pathDeal;
    const dealResponse = await fetch(encodedURL, getRequestOptions);
    if (dealResponse.ok) {
      const dealObject: any = await dealResponse.json();
      let allLars: LicenseAgreementRecord[] = dealObject.licenseAgreementRecords;
      let latestLar: LicenseAgreementRecord | null = null;
      if (allLars.length > 1) {
        allLars = allLars.sort((a, b) => SortUtils.sortDates(a.dateAccepted, b.dateAccepted));
        latestLar = allLars[allLars.length - 1];
      } else if (allLars.length > 0) {
        latestLar = allLars[0];
      }
      if (latestLar) {
        let prevAgreedState = EulaAgreedPreviously.unknown;
        if (latestLar.licenseType === LicenseAgreementType.trial || !latestLar.latestVersion) {
          prevAgreedState = EulaAgreedPreviously.no;
        } else {
          prevAgreedState = EulaAgreedPreviously.yes;
        } 
        handleEulaDataRequest({
          previouslyAgreed: prevAgreedState,
          isCustom: latestLar.customAgreement
        });
      } else {
        // if there's no license agreement record then EULA has not been agreed
        // and it is NOT a custom agreement
        handleEulaDataRequest({
          previouslyAgreed: EulaAgreedPreviously.no,
          isCustom: false
        });
      }
   
    } else {
      handleEulaDataRequest({
        previouslyAgreed: EulaAgreedPreviously.error,
        isCustom: false
      });
    }

    if (eulaRequestData.isCustom) {
      // don't update licenseId or EULA content if this is a custom
      return;
    }     

    const eulaResponse = await fetch(appURL + '/eula/latest', getRequestOptions);
    if (eulaResponse.ok) {
      const licenseAgreement: LicenseAgreement = await eulaResponse.json();
      const rawDOM = new DOMParser().parseFromString(licenseAgreement.content!, 'text/html');
      setLicenseAgreement({licenseId: licenseAgreement.licenseId, content: rawDOM.body.innerHTML });

    } else {
      const content= `
      <body>
        <div style="margin:1em">
          <p> Something went wrong when attempting to fetch EULA contents. </p>
          <p>Please contact DeltaXML support at <a style="color: ${DeltaXMLColors.FOUNTAIN_BLUE}" href="mailto:support@deltaxml.com?subject=Request to add card details">support@deltaxml.com</a> if problem persists</p>
        </div>
      </body>
      `;
      setLicenseAgreement({licenseId: null, content: content });
    }
  }

  React.useEffect(() => {
    fetchEulaContent();
  }, [])

  let eulaAgreementPrompt = '';
  switch (eulaRequestData.previouslyAgreed) {
    case EulaAgreedPreviously.error:
      eulaAgreementPrompt = '[ERROR] Unable to retrieve EULA agreement record for ' + dealId; 
      break;
    case EulaAgreedPreviously.unknown:
      eulaAgreementPrompt = 'Fetching EULA Data...'
      break;
    case EulaAgreedPreviously.yes:
      eulaAgreementPrompt = eulaRequestData.isCustom? 'Custom license agreement in place' : 'EULA agreed '; 
      break;
    case EulaAgreedPreviously.no:
      eulaAgreementPrompt = lineLicenseType === LicenseType.trial || lineLicenseType === LicenseType.trialCO ?
        ` for this free trial expiring on ${lineExpiry}`
        :
        ` for this product `; 
      break;
  }
  const eulaAgreedPreviously = eulaRequestData.previouslyAgreed === EulaAgreedPreviously.yes

  return (
    <React.Fragment>
      <Typography variant="body2" color="textSecondary">
      {eulaRequestData.previouslyAgreed === EulaAgreedPreviously.no &&
        <Checkbox value={isEulaAccepted || eulaAgreedPreviously} 
        onChange={() => {handleEulaCheckboxChange(); handleLicenseAccepted(licenseAgreement.licenseId!)}} 
        style={{ paddingLeft: '0px' }} />
      }
      {(eulaRequestData.previouslyAgreed !== EulaAgreedPreviously.unknown &&
        eulaRequestData.previouslyAgreed !== EulaAgreedPreviously.error &&
        eulaRequestData.previouslyAgreed !== EulaAgreedPreviously.yes &&
        !eulaRequestData.isCustom) ?
        <>
          Please check this box to accept our <span onClick={() => setDialogOpen(true)} className={classes.link}>Product Licence Agreement</span>
        </>
        :
        <>
          {eulaAgreementPrompt}
        </>
      }
      </Typography>
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <AppBar className={classes.appBar} position="static">
          <Toolbar>
            <Typography variant="h4" >End User License Agreement (EULA)</Typography>
          </Toolbar>
        </AppBar>
        <br />

        <DialogContent>
          <DialogContentText className={classes.contextText} dangerouslySetInnerHTML={{ __html: licenseAgreement.content! }} />
          {/* 
          <Typography variant="body1" color="textSecondary">
            Contents would go here. The quick brown fox jumped over the lazy dog.
          </Typography> */}
          <br />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}
export default withStyles(styles)(EulaCheckBox);